import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, Area } from "@/components/forms/Fields";
import { Disclaimer, INVESTOR_DISCLAIMER } from "@/components/Disclaimer";
import { submitLead, track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

export default function OpportunityDetail() {
  const { slug } = useParams();
  const [o, setO] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", budget: "", notes: "", consent: false, website: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  useSeo({ title: o ? `${o.title}, ${o.location} | Emerald Crown Opportunities` : "Opportunity | Emerald Crown", description: o?.summary || "Investor property opportunity from Emerald Crown Property Investments.", path: `/opportunities/${slug}` });

  useEffect(() => {
    track("page_view", { page: "opportunity_detail", slug });
    import("@/lib/api").then(({ api }) => api.get(`/opportunities/${slug}`).then(({ data }) => setO(data)).catch(() => setNotFound(true)));
  }, [slug]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone) return toast.error("Please complete the required fields.");
    if (!form.consent) return toast.error("Please provide your consent to continue.");
    setBusy(true);
    try {
      await submitLead({
        lead_type: "Investor", full_name: form.full_name, email: form.email, phone: form.phone,
        consent: form.consent, website: form.website, notes: form.notes,
        property_details: { opportunity: o.title, opportunity_location: o.location, strategy: o.strategy, budget: form.budget, interest_source: "Opportunity listing" },
      });
      track("opportunity_interest", { slug });
      setSubmitted(true);
    } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong."); } finally { setBusy(false); }
  };

  if (notFound) return (
    <div className="pt-40 pb-24 text-center"><p className="text-slate-600">Opportunity not found.</p><Link to="/opportunities" className="text-emerald-800 underline mt-3 inline-block">Back to opportunities</Link></div>
  );
  if (!o) return <div className="pt-40 pb-24 text-center text-slate-400">Loading…</div>;

  return (
    <div className="pt-20">
      <section className="relative h-[46vh] min-h-[340px]">
        {o.image && <img src={o.image} alt={o.title} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(2,20,15,0.94) 0%, rgba(2,20,15,0.4) 50%, rgba(2,20,15,0.1) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10">
          <Link to="/opportunities" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> All opportunities</Link>
          <span className="font-mono uppercase tracking-[0.2em] text-xs font-semibold text-white" style={{ textShadow: "0 1px 10px rgba(0,0,0,0.8)" }}>{o.strategy} · {o.status}</span>
          <h1 className="mt-2 text-4xl sm:text-5xl font-serif font-semibold text-white" data-testid="opportunity-title">{o.title}</h1>
          <div className="mt-2 flex items-center gap-1.5 text-white/85"><MapPin className="w-4 h-4" />{o.location}</div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
            {[["Price guide", o.price_guide], ["Strategy", o.strategy], ["Projected (illustrative)", o.projected_return]].filter(([, v]) => v).map(([l, v]) => (
              <div key={l} className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                <div className="text-xs text-slate-500">{l}</div><div className="mt-1 font-mono text-sm text-emerald-900">{v}</div>
              </div>
            ))}
          </div>
          {o.summary && <p className="text-lg text-slate-700 leading-relaxed">{o.summary}</p>}
          {o.description && <p className="mt-4 text-slate-600 leading-relaxed whitespace-pre-wrap">{o.description}</p>}
          <div className="mt-8"><Disclaimer text={INVESTOR_DISCLAIMER} testId="opportunity-disclaimer" /></div>
        </div>

        <div>
          <div id="register" className="bg-white rounded-2xl border border-slate-200/80 p-6 sticky top-24">
            {submitted ? (
              <div data-testid="opportunity-interest-success" className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-700 mx-auto" />
                <h3 className="mt-3 text-lg font-serif text-slate-900">Thanks — interest registered.</h3>
                <p className="mt-2 text-sm text-slate-600">We'll be in touch with the details. This is not an offer or a promise of any return.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="opportunity-interest-form">
                <h3 className="text-lg font-serif text-slate-900">Register your interest</h3>
                <input type="text" name="website" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <Field label="Full name" required value={form.full_name} onChange={set("full_name")} testId="opp-interest-name" />
                <Field label="Email" type="email" required value={form.email} onChange={set("email")} testId="opp-interest-email" />
                <Field label="Phone" required value={form.phone} onChange={set("phone")} testId="opp-interest-phone" />
                <Field label="Approximate budget" value={form.budget} onChange={set("budget")} testId="opp-interest-budget" />
                <Area label="Message (optional)" value={form.notes} onChange={set("notes")} rows={3} testId="opp-interest-notes" />
                <label className="flex items-start gap-2.5 text-xs text-slate-600">
                  <input type="checkbox" checked={form.consent} onChange={(e) => set("consent")(e.target.checked)} className="mt-0.5 w-4 h-4 accent-emerald-800" data-testid="opp-interest-consent" />
                  <span>I consent to being contacted about this opportunity and understand property investment carries risk with no guaranteed returns.</span>
                </label>
                <Button type="submit" disabled={busy} data-testid="opp-interest-submit" className="w-full bg-emerald-900 hover:bg-emerald-800 text-white rounded-full h-11">{busy ? "Sending…" : "Register interest"}</Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
